import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  BriefcaseBusiness,
  Cake,
  ChevronDown,
  ChevronUp,
  Clock3,
  Eye,
  EyeOff,
  GripVertical,
  Link as LinkIcon,
  MapPin,
  Phone,
  Plus,
  Star,
  Tag,
  Trash2,
  User,
  X,
} from "lucide-react";

import DynamicHeader from "../../Header/DynamicHeader";
import { getAccessToken } from "../../../awsAuth";
import { useAuth } from "../../../contexts/AuthContext";

import ProductsCatalog from "./modules/ProductsCatalog";
import ServicesCatalog from "./modules/ServicesCatalog";
import Vacancies from "./modules/Vacancies";
import BusinessListings from "./modules/BusinessListings";
import FAQ from "./modules/FAQ";
import BeforeAfter from "./modules/BeforeAfter";
import Booking from "./modules/Booking";
import BusinessModuleCreate from "./modules/BusinessModuleCreate";

import "./BusinessEditProfile.scss";

const DEFAULT_AVATAR =
  "https://cvanto-photos.s3.us-east-1.amazonaws.com/DefaultAvatar/avatar.png";

  const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

  const PROFILE_TYPES = [
  { value: "services", label: "Послуги" },
  { value: "products", label: "Товари" },
  { value: "handmade", label: "Ручна робота" },
  { value: "education", label: "Освіта" },
  { value: "influencer", label: "Інфлюенсер" },
  { value: "business", label: "Бізнес" },
];

const SERVICE_CATEGORIES = [
  { value: "Житло та нерухомість", label: "Житло та нерухомість" },
  { value: "Транспорт", label: "Транспорт" },
  { value: "Гаджети", label: "Гаджети" },
  { value: "Карʼєра", label: "Карʼєра" },
  { value: "Майстри та послуги", label: "Майстри та послуги" },
  { value: "Дім та затишок", label: "Дім та затишок" },
  { value: "Для саду та подвір'я", label: "Для саду та подвір'я" },
  { value: "Малеча", label: "Малеча" },
  { value: "Домашні улюбленці", label: "Домашні улюбленці" },
  { value: "Стиль", label: "Стиль" },
  { value: "Активність та спорт", label: "Активність та спорт" },
  { value: "Б’юті", label: "Б’юті" },
  { value: "Знання та Навчання", label: "Знання та Навчання" },
  { value: "Оренда та прокат", label: "Оренда та прокат" },
  { value: "Запчастини та аксесуари", label: "Запчастини та аксесуари" },
  { value: "Аксесуари", label: "Аксесуари" },
  { value: "Продукти та їжа", label: "Продукти та їжа" },
  { value: "Хобі", label: "Хобі" },
];

const MAX_SERVICE_CATEGORIES = 5;
const MAX_SERVICE_TAGS = 12;
const MAX_LINKS = 3;

const DEFAULT_ACTION_BUTTONS = {
  call: true,
  message: true,
  booking: true,
  share: true,
};

function normalizeTag(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  return s.replace(/\s+/g, " ").slice(0, 32);
}

function isValidTag(s) {
  if (!s) return false;
  return /^[\p{L}\p{N}\s.'’\-]+$/u.test(s);
}

function normalizeUrl(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  return "https://" + s.replace(/^\/+/, "");
}

function isValidHttpUrl(u) {
  try {
    const x = new URL(normalizeUrl(u));
    return x.protocol === "http:" || x.protocol === "https:";
  } catch {
    return false;
  }
}

function isoToDisplayDate(value) {
  const s = String(value || "").trim();
  if (!s) return "";
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function displayDateToIso(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  if (digits.length !== 8) return "";
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);
  return `${yyyy}-${mm}-${dd}`;
}

function formatBirthDateInput(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 8);
  const dd = digits.slice(0, 2);
  const mm = digits.slice(2, 4);
  const yyyy = digits.slice(4, 8);

  if (digits.length <= 2) return dd;
  if (digits.length <= 4) return `${dd}.${mm}`;
  return `${dd}.${mm}.${yyyy}`;
}

const MODULES = [
  {
    key: "products",
    icon: "🛍",
    title: "Каталог товарів",
    desc: "Товари, фото, ціни, кнопка замовлення",
    kind: "large",
  },
  {
    key: "services",
    icon: "🧾",
    title: "Каталог послуг",
    desc: "Послуги, ціни, тривалість, запис",
    kind: "large",
  },
  {
  key: "listings",
  icon: "📢",
  title: "Оголошення",
  desc: "Звичайні оголошення бізнесу у профілі",
  kind: "large",
  },
  {
  key: "posts",
  icon: "🖼",
  title: "Пости",
  desc: "Публікації, новини та оновлення бізнесу",
  kind: "large",
  },
  {
    key: "vacancies",
    icon: "💼",
    title: "Вакансії",
    desc: "Відкриті позиції у вашому бізнесі",
    kind: "small",
  },
  {
    key: "booking",
    icon: "📅",
    title: "Онлайн-запис",
    desc: "Слоти, бронювання, календар",
    kind: "small",
  },
  {
    key: "faq",
    icon: "❓",
    title: "FAQ",
    desc: "Популярні питання та відповіді",
    kind: "small",
  },
  {
    key: "beforeAfter",
    icon: "✨",
    title: "До / після",
    desc: "Кейси, результати, трансформації",
    kind: "large",
  },
];

const DEFAULT_QUICK_NAV_ORDER = MODULES.map(
  (module) => module.key
);

const DEFAULT_ENABLED = MODULES.reduce((acc, module) => {
  acc[module.key] = false;
  return acc;
}, {});

const WORKING_HOURS_DAYS = [
  { key: "mon", label: "Пн" },
  { key: "tue", label: "Вт" },
  { key: "wed", label: "Ср" },
  { key: "thu", label: "Чт" },
  { key: "fri", label: "Пт" },
  { key: "sat", label: "Сб" },
  { key: "sun", label: "Нд" },
];

const DAY_LABEL_TO_KEY = {
  Пн: "mon",
  Вт: "tue",
  Ср: "wed",
  Чт: "thu",
  Пт: "fri",
  Сб: "sat",
  Нд: "sun",
};

const DAY_KEY_TO_LABEL = {
  mon: "Пн",
  tue: "Вт",
  wed: "Ср",
  thu: "Чт",
  fri: "Пт",
  sat: "Сб",
  sun: "Нд",
};

function safeObj(v) {
  return v && typeof v === "object" ? v : {};
}

function makeTimeOptions(stepMinutes = 30) {
  const out = [];

  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += stepMinutes) {
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }

  out.push("00:00");
  return Array.from(new Set(out));
}

const TIME_OPTIONS = makeTimeOptions(30);

function normalizeTimeStr(v, fallback) {
  const s = String(v || "").trim();
  if (/^\d{2}:\d{2}$/.test(s)) return s;
  return fallback;
}

function scheduleArrayToHoursMap(scheduleArr) {
  const map = {
    mon: { enabled: false, start: "10:00", end: "18:00" },
    tue: { enabled: false, start: "10:00", end: "18:00" },
    wed: { enabled: false, start: "10:00", end: "18:00" },
    thu: { enabled: false, start: "10:00", end: "18:00" },
    fri: { enabled: false, start: "10:00", end: "18:00" },
    sat: { enabled: false, start: "10:00", end: "18:00" },
    sun: { enabled: false, start: "10:00", end: "18:00" },
  };

  const arr = Array.isArray(scheduleArr) ? scheduleArr : [];

  for (const row of arr) {
    const key = DAY_LABEL_TO_KEY[String(row?.day || "").trim()];
    if (!key) continue;

    map[key] = {
      enabled: true,
      start: normalizeTimeStr(row?.from, "10:00"),
      end: normalizeTimeStr(row?.to, "18:00"),
    };
  }

  return map;
}

function hoursMapToScheduleArray(hoursMap) {
  const h = safeObj(hoursMap);
  const out = [];

  for (const d of WORKING_HOURS_DAYS) {
    const v = safeObj(h[d.key]);
    if (!v.enabled) continue;

    out.push({
      day: DAY_KEY_TO_LABEL[d.key],
      from: normalizeTimeStr(v.start, "10:00"),
      to: normalizeTimeStr(v.end, "18:00"),
    });
  }

  return out;
}

function getTodayScheduleLabel(scheduleArr) {
  const jsDay = new Date().getDay();

  const dayKeyByJsDay = {
    0: "sun",
    1: "mon",
    2: "tue",
    3: "wed",
    4: "thu",
    5: "fri",
    6: "sat",
  };

  const todayLabel = DAY_KEY_TO_LABEL[dayKeyByJsDay[jsDay]];
  const today = (Array.isArray(scheduleArr) ? scheduleArr : []).find(
    (row) => row.day === todayLabel
  );

  if (!today) return "Зачинено сьогодні";
  return `Відчинено до ${today.to}`;
}

function normalizeOrder(order) {
  const incoming = Array.isArray(order) ? order : [];
  const clean = incoming.filter((key) => MODULES.some((m) => m.key === key));
  const unique = [...new Set(clean)];
  const missing = MODULES.map((m) => m.key).filter(
    (key) => !unique.includes(key)
  );

  return [...unique, ...missing];
}

function normalizeQuickNavOrder(order) {
  const incoming = Array.isArray(order)
    ? order
    : [];

  const clean = incoming.filter((key) =>
    MODULES.some((module) => module.key === key)
  );

  return [...new Set(clean)];
}

function RatingStars({ value = 0 }) {
  const normalized = Math.max(0, Math.min(10, Number(value || 0)));
  const activeStars = Math.round(normalized / 2);

  return (
    <div className="business-edit__stars" aria-label={`Рейтинг ${normalized}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= activeStars ? "is-on" : ""}>
          <Star size={14} strokeWidth={2.4} />
        </span>
      ))}
    </div>
  );
}

function SimpleModulePreview({ module }) {
  const meta = {
  booking: [
    "Онлайн-запис",
    "Клієнти зможуть обрати час і записатись",
  ],

  vacancies: [
    "Вакансії",
    "Додай відкриті позиції для працівників",
  ],

  posts: [
    "Пости",
    "Публікації створюються через стандартний редактор постів Cvanto",
  ],

  faq: [
    "FAQ",
    "Відповіді на часті питання клієнтів",
  ],

  beforeAfter: [
    "До / після",
    "Покажи результат роботи у форматі кейсів",
  ],
};

  const [title, text] = meta[module.key] || [module.title, module.desc];

  return (
    <div className="beSimplePreview">
      <div className="beSimplePreview__icon">{module.icon}</div>

      <div className="beSimplePreview__text">
        <strong>{title}</strong>
        <span>{text}</span>
      </div>
    </div>
  );
}

function ModuleWidget({
  module,
  active,
  opened,
  count,
  index,
  total,
  dragging,
  onToggle,
  onOpen,
  onMove,
  onRemove,
  onDragStart,
  onDragOver,
  onDragEnd,
  children,
}) {
  const pointerIdRef = useRef(null);
  const lastTargetRef = useRef("");

  const countLabel =
    typeof count === "number"
      ? count === 1
        ? "1 елемент"
        : `${count} елементів`
      : "Налаштовано";

  const handlePointerDown = (event) => {
    if (event.pointerType === "mouse") return;

    event.preventDefault();
    event.stopPropagation();

    pointerIdRef.current = event.pointerId;
    lastTargetRef.current = module.key;

    event.currentTarget.setPointerCapture?.(
      event.pointerId
    );

    onDragStart(module.key);
  };

  const handlePointerMove = (event) => {
    if (
      event.pointerType === "mouse" ||
      pointerIdRef.current !== event.pointerId
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const hoveredElement = document
      .elementFromPoint(
        event.clientX,
        event.clientY
      )
      ?.closest("[data-module-key]");

    const targetKey =
      hoveredElement?.getAttribute(
        "data-module-key"
      );

    if (
      !targetKey ||
      targetKey === lastTargetRef.current
    ) {
      return;
    }

    lastTargetRef.current = targetKey;
    onDragOver(targetKey);
  };

  const handlePointerEnd = (event) => {
    if (
      event.pointerType === "mouse" ||
      pointerIdRef.current !== event.pointerId
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    event.currentTarget.releasePointerCapture?.(
      event.pointerId
    );

    pointerIdRef.current = null;
    lastTargetRef.current = "";

    onDragEnd();
  };

  const handleMouseDragStart = (event) => {
    event.stopPropagation();

    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData(
      "text/plain",
      module.key
    );

    onDragStart(module.key);
  };

  const handleMouseDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();

    event.dataTransfer.dropEffect = "move";

    onDragOver(module.key);
  };

  const handleMouseDragEnd = () => {
    onDragEnd();
  };

  return (
    <section
      className={[
        "beModuleWidget",
        active ? "is-active" : "is-hidden",
        opened ? "is-open" : "",
        dragging ? "is-dragging" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-module-key={module.key}
      style={{
        viewTransitionName: `business-module-${module.key}`,
      }}
      onDragOver={handleMouseDragOver}
      onDrop={(event) => {
        event.preventDefault();
        onDragEnd();
      }}
    >
      <div className="beModuleWidget__pill">
        <button
          type="button"
          className="beModuleWidget__main"
          onClick={() => onOpen(module.key)}
          aria-expanded={opened}
        >
          <span
            className={[
              "beModuleWidget__icon",
              `beModuleWidget__icon--${module.key}`,
            ].join(" ")}
          >
            {module.icon}
          </span>

          <span className="beModuleWidget__text">
            <strong>{module.title}</strong>

            <span className="beModuleWidget__meta">
              <i
                className={
                  active ? "is-active" : ""
                }
              />

              <span>
                {active
                  ? "У профілі"
                  : "Приховано"}
              </span>

              <b>·</b>

              <span>{countLabel}</span>
            </span>
          </span>
        </button>

        <div className="beModuleWidget__controls">
          <div className="beModuleWidget__desktopMove">
            <button
              type="button"
              disabled={index === 0}
              onClick={() =>
                onMove(module.key, "up")
              }
              aria-label="Перемістити вище"
            >
              <ChevronUp
                size={17}
                strokeWidth={2.25}
              />
            </button>

            <button
              type="button"
              disabled={index === total - 1}
              onClick={() =>
                onMove(module.key, "down")
              }
              aria-label="Перемістити нижче"
            >
              <ChevronDown
                size={17}
                strokeWidth={2.25}
              />
            </button>
          </div>

          <button
            type="button"
            className={[
              "beModuleWidget__visibility",
              active ? "is-active" : "",
            ].join(" ")}
            onClick={() => onToggle(module.key)}
            aria-label={
              active
                ? "Сховати з профілю"
                : "Показати у профілі"
            }
          >
            {active ? (
              <Eye
                size={19}
                strokeWidth={2.1}
              />
            ) : (
              <EyeOff
                size={19}
                strokeWidth={2.1}
              />
            )}
          </button>

          <button
            type="button"
            className={[
              "beModuleWidget__chevron",
              opened ? "is-open" : "",
            ].join(" ")}
            onClick={() => onOpen(module.key)}
            aria-label={
              opened
                ? "Згорнути модуль"
                : "Відкрити модуль"
            }
          >
            <ChevronDown
              size={18}
              strokeWidth={2.2}
            />
          </button>

          <button
            type="button"
            className="beDragHandle"
            draggable
            onDragStart={handleMouseDragStart}
            onDragEnd={handleMouseDragEnd}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onPointerCancel={handlePointerEnd}
            aria-label="Затисніть і перетягніть"
          >
            <GripVertical
              size={20}
              strokeWidth={2.15}
            />
          </button>
        </div>
      </div>

      <div
        className={[
          "beModuleWidget__bodyWrap",
          opened ? "is-open" : "",
        ].join(" ")}
      >
        <div className="beModuleWidget__body">
          <div className="beModuleWidget__moduleSettings">
            <button
              type="button"
              className={
                active ? "is-active" : ""
              }
              onClick={() => onToggle(module.key)}
            >
              {active ? (
                <Eye
                  size={16}
                  strokeWidth={2.2}
                />
              ) : (
                <EyeOff
                  size={16}
                  strokeWidth={2.2}
                />
              )}

              <span>
                {active
                  ? "Показується у профілі"
                  : "Приховано у профілі"}
              </span>
            </button>

            <button
              type="button"
              className="is-danger"
              onClick={() =>
                onRemove(module.key)
              }
            >
              <Trash2
                size={16}
                strokeWidth={2.2}
              />

              <span>Видалити модуль</span>
            </button>
          </div>

          {children}
        </div>
      </div>
    </section>
  );
}

function ProfileAccordionSection({
  icon,
  title,
  description,
  open,
  onToggle,
  badge,
  children,
  className = "",
}) {
  return (
    <section
      className={[
        "beProfileAccordion",
        open ? "is-open" : "is-collapsed",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <button
        type="button"
        className="beProfileAccordion__head"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="beProfileAccordion__icon">
          {icon}
        </span>

        <span className="beProfileAccordion__heading">
          <span className="beProfileAccordion__titleRow">
            <strong>{title}</strong>

            {badge && (
              <span className="beProfileAccordion__badge">
                {badge}
              </span>
            )}
          </span>

          <span className="beProfileAccordion__description">
            {description}
          </span>
        </span>

        <span
          className={[
            "beProfileAccordion__chevron",
            open ? "is-open" : "",
          ].join(" ")}
        >
          <ChevronDown size={20} strokeWidth={2.4} />
        </span>
      </button>

      <div
        className={[
          "beProfileAccordion__bodyWrap",
          open ? "is-open" : "",
        ].join(" ")}
      >
        <div className="beProfileAccordion__body">
          {children}
        </div>
      </div>
    </section>
  );
}

export default function BusinessEditProfile() {
  const navigate = useNavigate();
  const { userId } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [description, setDescription] = useState("");
  const [avatarPreview, setAvatarPreview] = useState(DEFAULT_AVATAR);
  const [avatarFile, setAvatarFile] = useState(null);
  const [location, setLocation] = useState("");
  const [phone, setPhone] = useState("");
  const [showLocation, setShowLocation] = useState(true);
  const [actionButtons, setActionButtons] = useState(
  DEFAULT_ACTION_BUTTONS
);

  const [gender, setGender] = useState("");
  const [profileType, setProfileType] = useState("business");
  const [birthDate, setBirthDate] = useState("");

  const [serviceCategories, setServiceCategories] = useState([]);
  const [serviceTags, setServiceTags] = useState([]);
  const [serviceTagDraft, setServiceTagDraft] = useState("");

  const [links, setLinks] = useState([]);
  const [linkLabel, setLinkLabel] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  const [isBusinessPlan, setIsBusinessPlan] = useState(true);

  const [rating, setRating] = useState(0);
  const [reviewsCount, setReviewsCount] = useState(0);
  const [, setProductsCount] = useState(0);
  const [servicesCount, setServicesCount] = useState(0);

  const [accentColor, setAccentColor] = useState("#4176FC");
  const [layout, setLayout] = useState("appstore");

  const [modulesOrder, setModulesOrder] = useState(MODULES.map((m) => m.key));
  const [enabledModules, setEnabledModules] = useState(DEFAULT_ENABLED);
  const [quickNavEnabled, setQuickNavEnabled] =
  useState(false);
  const [quickNavOrder, setQuickNavOrder] =
  useState(DEFAULT_QUICK_NAV_ORDER);

  const [
    quickNavDraggingKey,
    setQuickNavDraggingKey,
  ] = useState("");
  const [aboutTitle, setAboutTitle] = useState("Про бізнес");
  const [aboutText, setAboutText] = useState("");
  const [schedule, setSchedule] = useState([]);

  const [productsDraft, setProductsDraft] = useState([]);
  const [servicesDraft, setServicesDraft] = useState([]);
  const [vacanciesDraft, setVacanciesDraft] = useState([]);
  const [listingsDraft, setListingsDraft] = useState([]);
  const [postsDraft, setPostsDraft] = useState([]);
  const [draggingKey, setDraggingKey] = useState("");
  const [modulePickerOpen, setModulePickerOpen] = useState(false);

  const [openedModuleKey, setOpenedModuleKey] = useState("");

  const [personalSectionOpen, setPersonalSectionOpen] = useState(true);
  const [businessSectionOpen, setBusinessSectionOpen] = useState(false);

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createModuleType, setCreateModuleType] = useState("products");

  const sortedModules = useMemo(() => {
    return normalizeOrder(modulesOrder)
      .map((key) => MODULES.find((m) => m.key === key))
      .filter(Boolean)
      .filter((module) => enabledModules[module.key]);
  }, [modulesOrder, enabledModules]);

  const quickNavModules = useMemo(() => {
  const selectedQuickNavKeys = new Set(
    normalizeQuickNavOrder(quickNavOrder)
  );

  return normalizeOrder(modulesOrder)
    .filter((key) => selectedQuickNavKeys.has(key))
    .map((key) =>
      MODULES.find((module) => module.key === key)
    )
    .filter(Boolean)
    .filter(
      (module) =>
        enabledModules[module.key] === true
    );
}, [
  modulesOrder,
  quickNavOrder,
  enabledModules,
]);

  const workingHoursMap = useMemo(() => {
    return scheduleArrayToHoursMap(schedule);
  }, [schedule]);

  const scheduleStatusText = useMemo(() => {
    return getTodayScheduleLabel(schedule);
  }, [schedule]);

  const setWorkingHoursMap = useCallback((nextMap) => {
    setSchedule(hoursMapToScheduleArray(nextMap));
  }, []);

  const toggleWorkingDay = useCallback(
    (dayKey) => {
      const m = safeObj(workingHoursMap);
      const v = safeObj(m[dayKey]);

      setWorkingHoursMap({
        ...m,
        [dayKey]: {
          enabled: !v.enabled,
          start: normalizeTimeStr(v.start, "10:00"),
          end: normalizeTimeStr(v.end, "18:00"),
        },
      });
    },
    [workingHoursMap, setWorkingHoursMap]
  );

  const setWorkingTime = useCallback(
    (dayKey, which, value) => {
      setWorkingHoursMap({
        ...workingHoursMap,
        [dayKey]: {
          ...safeObj(workingHoursMap[dayKey]),
          enabled: true,
          [which]: normalizeTimeStr(value, which === "start" ? "10:00" : "18:00"),
        },
      });
    },
    [workingHoursMap, setWorkingHoursMap]
  );

  const clearWorkingDay = useCallback(
    (dayKey) => {
      setWorkingHoursMap({
        ...workingHoursMap,
        [dayKey]: {
          enabled: false,
          start: "10:00",
          end: "18:00",
        },
      });
    },
    [workingHoursMap, setWorkingHoursMap]
  );

  useEffect(() => {
    let alive = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const token = await getAccessToken();

        const profileRes = await fetch(`${API_URL}/api/business/profile/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await profileRes.json().catch(() => ({}));

        if (!profileRes.ok) {
          throw new Error(data?.error || "Не вдалося завантажити бізнес-профіль");
        }

        const profile = data?.profile || {};
        const userBase = data?.userBase || {};

        if (!alive) return;

        setTitle(userBase.username || profile.title || profile.hero?.title || "");
        setSubtitle(userBase.bio || profile.subtitle || profile.hero?.subtitle || "");
        setDescription(profile.description || "");
        setAvatarPreview(userBase.avatar || DEFAULT_AVATAR);
         setLocation(userBase.location || "");
        setPhone(userBase.phone || "");
        setShowLocation(userBase.showLocation !== false);
        setActionButtons({
          ...DEFAULT_ACTION_BUTTONS,
          ...safeObj(profile.actionButtons),
        });

                const businessFromBackend =
          userBase.isBusiness === true ||
          userBase.plan === "business" ||
          userBase.entitlements?.businessModules === true;

        setIsBusinessPlan(businessFromBackend);

        setGender(userBase.gender || "");
        setProfileType(
          businessFromBackend
            ? userBase.profileType && userBase.profileType !== "personal"
              ? userBase.profileType
              : "business"
            : userBase.profileType || ""
        );

        setBirthDate(isoToDisplayDate(userBase.birthDate || ""));

        setServiceCategories(
          Array.isArray(userBase.serviceCategories)
            ? userBase.serviceCategories.slice(0, MAX_SERVICE_CATEGORIES)
            : []
        );

        setServiceTags(
          Array.isArray(userBase.serviceTags)
            ? userBase.serviceTags.map(normalizeTag).filter(Boolean).slice(0, MAX_SERVICE_TAGS)
            : []
        );

        setLinks(
          Array.isArray(userBase.links)
            ? userBase.links
                .map((x) => ({
                  label: String(x?.label || "").trim().slice(0, 50),
                  url: String(x?.url || "").trim(),
                }))
                .filter((x) => x.url)
                .slice(0, MAX_LINKS)
            : []
        );

        setAccentColor(profile.theme?.accentColor || "#4176FC");
        setLayout(profile.theme?.layout || "appstore");

        const savedEnabledModules = {
          ...DEFAULT_ENABLED,
          ...(profile.enabledModules || {}),
        };

        const savedOrder =
          Array.isArray(profile.modulesOrder) && profile.modulesOrder.length
            ? normalizeOrder(profile.modulesOrder)
            : [];

        setEnabledModules(savedEnabledModules);
        setModulesOrder(savedOrder.filter((key) => savedEnabledModules[key]));

        setQuickNavEnabled(
          profile.quickNavEnabled === true
        );

        const savedQuickNavOrder =
          Array.isArray(profile.quickNavOrder) &&
          profile.quickNavOrder.length
            ? normalizeQuickNavOrder(
                profile.quickNavOrder
              )
            : DEFAULT_QUICK_NAV_ORDER;

        setQuickNavOrder(savedQuickNavOrder);

        setAboutTitle(profile.about?.title || "Про бізнес");
        setAboutText(profile.about?.text || "");
        setSchedule(Array.isArray(profile.schedule) ? profile.schedule : []);

        setLoading(false);

        fetch(`${API_URL}/api/trust/${encodeURIComponent(userId)}/details`)
          .then((r) => r.json())
          .then((trustData) => {
            if (!alive) return;

            setRating(
              Number(
                trustData?.trustLevel ||
                  (trustData?.trustScore ? trustData.trustScore / 10 : userBase.rating || 0)
              )
            );

            setReviewsCount(Number(userBase.reviewsCount || 0));
          })
          .catch(() => {});

        fetch(`${API_URL}/api/listings/user/${encodeURIComponent(userId)}`)
        .then((r) => r.json())
        .then((listings) => {
          if (!alive) return;

          const allListings = Array.isArray(listings) ? listings : [];

          const normalListings = allListings.filter((item) => {
            const category = String(item.category || "").toLowerCase();
            const moduleKey = String(
              item.businessModule || item.moduleKey || item.entityType || ""
            ).toLowerCase();

            return (
              category !== "карʼєра" &&
              category !== "кар'єра" &&
              moduleKey !== "vacancies" &&
              moduleKey !== "business_vacancy"
            );
          });

          setListingsDraft(normalListings);
          setServicesCount(Number(userBase.servicesCount || 0));
        })
        .catch(() => {});

        fetch(`${API_URL}/api/business/products/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
          .then((r) => r.json())
          .then((productsData) => {
            if (!alive) return;

            const items = Array.isArray(productsData?.items) ? productsData.items : [];

            setProductsDraft(items);
            setProductsCount(items.length);
          })
          .catch(() => {});

        fetch(`${API_URL}/api/business/services/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
          .then((r) => r.json())
          .then((servicesData) => {
            if (!alive) return;

            const items = Array.isArray(servicesData?.items) ? servicesData.items : [];

            setServicesDraft(items);
            setServicesCount(items.length);
          })
          .catch(() => {});

        fetch(`${API_URL}/api/business/vacancies/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
          .then((r) => r.json())
          .then((vacanciesData) => {
            if (!alive) return;

            const items = Array.isArray(vacanciesData?.items) ? vacanciesData.items : [];

            setVacanciesDraft(items);
          })
          .catch(() => {});
          fetch(
  `${API_URL}/api/posts/user/${encodeURIComponent(userId)}`,
  {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  }
)
  .then((response) => response.json())
  .then((postsData) => {
    if (!alive) return;

    const items = Array.isArray(postsData)
      ? postsData
      : Array.isArray(postsData?.items)
      ? postsData.items
      : [];

    setPostsDraft(items);
  })
  .catch(() => {
    if (!alive) return;
    setPostsDraft([]);
  });
      } catch (e) {
        console.error(e);
        if (alive) setError(e.message || "Помилка завантаження");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();

    return () => {
      alive = false;
    };
  }, [userId]);

  const toggleActionButton = (key) => {
  setActionButtons((prev) => ({
    ...prev,
    [key]: !prev[key],
  }));
};

const toggleQuickNavModule = (key) => {
  setQuickNavOrder((previous) => {
    const normalized =
      normalizeQuickNavOrder(previous);

    if (normalized.includes(key)) {
      return normalized.filter(
        (item) => item !== key
      );
    }

    return [...normalized, key];
  });
};

const startQuickNavDrag = (key) => {
  setQuickNavDraggingKey(key);
};

const moveQuickNavDrag = (targetKey) => {
  if (
    !quickNavDraggingKey ||
    !targetKey ||
    quickNavDraggingKey === targetKey
  ) {
    return;
  }

  setQuickNavOrder((previous) => {
    const selectedOrder =
      normalizeQuickNavOrder(previous);

    const fromIndex =
      selectedOrder.indexOf(
        quickNavDraggingKey
      );

    const targetIndex =
      selectedOrder.indexOf(targetKey);

    if (
      fromIndex === -1 ||
      targetIndex === -1 ||
      fromIndex === targetIndex
    ) {
      return previous;
    }

    const next = [...selectedOrder];

    const [movedKey] = next.splice(
      fromIndex,
      1
    );

    next.splice(
      targetIndex,
      0,
      movedKey
    );

    return next;
  });
};

const finishQuickNavDrag = () => {
  setQuickNavDraggingKey("");
};

const getModuleCount = (key) => {
  if (key === "products") {
    return productsDraft.length;
  }

  if (key === "services") {
    return servicesDraft.length;
  }

  if (key === "listings") {
    return listingsDraft.length;
  }

  if (key === "vacancies") {
    return vacanciesDraft.length;
  }

  if (key === "posts") {
    return postsDraft.length;
  }

  return null;
};

const toggleOpenedModule = (key) => {
  setOpenedModuleKey((current) =>
    current === key ? "" : key
  );
};

  const toggleModule = (key) => {
    setEnabledModules((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const moveModule = (key, dir) => {
    setModulesOrder((prev) => {
      const next = normalizeOrder(prev);
      const index = next.indexOf(key);
      const target = dir === "up" ? index - 1 : index + 1;

      if (index === -1 || target < 0 || target >= next.length) return prev;

      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeModule = async (key) => {
  const labels = {
    products: "Каталог товарів",
    services: "Каталог послуг",
    listings: "Оголошення",
    posts: "Пости",
    vacancies: "Вакансії",
    booking: "Онлайн-запис",
    faq: "FAQ",
    beforeAfter: "До / Після",
  };

  const destructiveKeys = [
    "products",
    "services",
    "listings",
    "posts",
    "vacancies",
    "beforeAfter",
  ];

  const isDestructive =
    destructiveKeys.includes(key);

  const label =
    labels[key] || "цей модуль";

  const message = isDestructive
    ? `Безповоротно видалити модуль «${label}»?\n\nУсі дані та фотографії цього модуля будуть назавжди видалені. Відновити їх буде неможливо.`
    : `Видалити модуль «${label}» з бізнес-профілю?`;

  const confirmed =
    window.confirm(message);

  if (!confirmed) return;

  try {
    setSaving(true);
    setError("");

    const token =
      await getAccessToken();

    const response = await fetch(
      `${API_URL}/api/business/modules/${encodeURIComponent(
        key
      )}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    const data = await response
      .json()
      .catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        data?.error ||
          "Не вдалося видалити модуль"
      );
    }

    if (key === "products") {
      setProductsDraft([]);
      setProductsCount(0);
    }

    if (key === "services") {
      setServicesDraft([]);
      setServicesCount(0);
    }

    if (key === "vacancies") {
      setVacanciesDraft([]);
    }

    if (key === "listings") {
      setListingsDraft([]);
    }

    if (key === "posts") {
      setPostsDraft([]);
    }

    setEnabledModules((previous) => ({
      ...previous,
      [key]: false,
    }));

    setModulesOrder((previous) =>
      normalizeOrder(previous).filter(
        (item) => item !== key
      )
    );

    setQuickNavOrder((previous) =>
      normalizeQuickNavOrder(
        previous
      ).filter(
        (item) => item !== key
      )
    );

    setOpenedModuleKey("");

    setSuccess(
      `Модуль «${label}» видалено безповоротно`
    );
  } catch (error) {
    console.error(
      "[BusinessEditProfile] module delete:",
      error
    );

    setError(
      error?.message ||
        "Не вдалося видалити модуль"
    );
  } finally {
    setSaving(false);
  }
};

  const addModule = (key) => {
    const exists = MODULES.some((module) => module.key === key);
    if (!exists) return;

    setEnabledModules((prev) => ({
      ...prev,
      [key]: true,
    }));

    setModulesOrder((prev) => {
      const clean = normalizeOrder(prev).filter((item) => item !== key);
      return [...clean, key];
    });

    setModulePickerOpen(false);
  };

  const onDragStart = useCallback((key) => {
  setOpenedModuleKey("");
  setDraggingKey(key);

  document.body.classList.add(
    "be-is-reordering"
  );
}, []);

const reorderModulesLive = useCallback(
  (dragKey, targetKey) => {
    if (
      !dragKey ||
      !targetKey ||
      dragKey === targetKey
    ) {
      return;
    }

    const updateOrder = () => {
      setModulesOrder((previousOrder) => {
        const nextOrder =
          normalizeOrder(previousOrder);

        const fromIndex =
          nextOrder.indexOf(dragKey);

        const targetIndex =
          nextOrder.indexOf(targetKey);

        if (
          fromIndex === -1 ||
          targetIndex === -1 ||
          fromIndex === targetIndex
        ) {
          return previousOrder;
        }

        const [movedKey] = nextOrder.splice(
          fromIndex,
          1
        );

        nextOrder.splice(
          targetIndex,
          0,
          movedKey
        );

        return nextOrder;
      });
    };

    if (
      typeof document !== "undefined" &&
      typeof document.startViewTransition ===
        "function"
    ) {
      document.startViewTransition(() => {
        flushSync(updateOrder);
      });

      return;
    }

    updateOrder();
  },
  []
);

const onDragOver = useCallback(
  (targetKey) => {
    if (!draggingKey) return;

    reorderModulesLive(
      draggingKey,
      targetKey
    );
  },
  [draggingKey, reorderModulesLive]
);

const onDragEnd = useCallback(() => {
  setDraggingKey("");

  document.body.classList.remove(
    "be-is-reordering"
  );
}, []);

  const openProductCreate = () => {
    setCreateModuleType("products");
    setCreateModalOpen(true);
  };

  const openServiceCreate = () => {
    setCreateModuleType("services");
    setCreateModalOpen(true);
  };

  const openVacancyCreate = () => {
    navigate("/add-ad?businessModule=vacancies");
  };

  const handleModuleCreate = (payload) => {
    if (payload?.entityType === "business_product" || payload?.moduleKey === "products") {
      setProductsDraft((prev) => [payload, ...prev]);
      setProductsCount((prev) => Number(prev || 0) + 1);
    }

    if (payload?.entityType === "business_service" || payload?.moduleKey === "services") {
      setServicesDraft((prev) => [payload, ...prev]);
      setServicesCount((prev) => Number(prev || 0) + 1);
    }

    if (payload?.entityType === "business_vacancy" || payload?.moduleKey === "vacancies") {
      setVacanciesDraft((prev) => [payload, ...prev]);
    }

    setCreateModalOpen(false);
  };

  const onAvatarPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

    const handleProfileTypePick = (nextType) => {
    if (isBusinessPlan && nextType === "personal") return;
    setProfileType(nextType);
  };

  const toggleServiceCategory = (catValue) => {
    const v = String(catValue || "").trim();
    if (!v) return;

    setServiceCategories((prev) => {
      const has = prev.includes(v);
      if (has) return prev.filter((x) => x !== v);

      if (prev.length >= MAX_SERVICE_CATEGORIES) {
        setError(`Максимум ${MAX_SERVICE_CATEGORIES} категорій спеціалізації`);
        return prev;
      }

      return [...prev, v];
    });
  };

  const addServiceTag = () => {
    const v = normalizeTag(serviceTagDraft);

    if (!v) return;

    if (!isValidTag(v)) {
      setError("Некоректний тег");
      return;
    }

    setServiceTags((prev) => {
      const exists = prev.some((x) => String(x).toLowerCase() === v.toLowerCase());

      if (exists) {
        setServiceTagDraft("");
        return prev;
      }

      if (prev.length >= MAX_SERVICE_TAGS) {
        setError(`Максимум ${MAX_SERVICE_TAGS} тегів`);
        return prev;
      }

      return [...prev, v];
    });

    setServiceTagDraft("");
  };

  const removeServiceTagAt = (idx) => {
    setServiceTags((prev) => prev.filter((_, i) => i !== idx));
  };

  const addLink = () => {
    const label = String(linkLabel || "").trim().slice(0, 50);
    const urlRaw = String(linkUrl || "").trim();

    if (!urlRaw) {
      setError("Введіть URL посилання");
      return;
    }

    if (links.length >= MAX_LINKS) {
      setError(`Максимум ${MAX_LINKS} посилання`);
      return;
    }

    if (!isValidHttpUrl(urlRaw)) {
      setError("Некоректне посилання");
      return;
    }

    const url = normalizeUrl(urlRaw);

    if (links.some((l) => l.url === url)) {
      setError("Таке посилання вже додано");
      return;
    }

    setLinks((prev) => [
      ...prev,
      {
        label: label || new URL(url).hostname,
        url,
      },
    ]);

    setLinkLabel("");
    setLinkUrl("");
  };

  const removeLinkAt = (idx) => {
    setLinks((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleBirthDateChange = (e) => {
    setBirthDate(formatBirthDateInput(e.target.value));
  };

  const save = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");

      const token = await getAccessToken();
      let avatarUrl = avatarPreview || DEFAULT_AVATAR;

      if (avatarFile) {
        const formData = new FormData();
        formData.append("avatar", avatarFile);

        const avatarRes = await fetch(`${API_URL}/upload/avatar`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        const avatarData = await avatarRes.json().catch(() => ({}));

        if (!avatarRes.ok) {
          throw new Error(avatarData?.error || "Не вдалося завантажити аватарку");
        }

        avatarUrl = avatarData.avatarUrl;
      }

      const normalizedBirthDate = displayDateToIso(birthDate);

const accountPayload = {
  username: title,
  avatar: avatarUrl,
  bio: subtitle,
  location,
  phone,
  showLocation,
  gender,
  profileType: isBusinessPlan
    ? profileType && profileType !== "personal"
      ? profileType
      : "business"
    : profileType,

  links: links
    .map((x) => ({
      label: String(x?.label || "").trim().slice(0, 50),
      url: normalizeUrl(String(x?.url || "").trim()),
    }))
    .filter((x) => x.url && isValidHttpUrl(x.url))
    .slice(0, MAX_LINKS),

  serviceCategories: serviceCategories.slice(
    0,
    MAX_SERVICE_CATEGORIES
  ),

  serviceTags: serviceTags
    .map(normalizeTag)
    .filter((x) => x && isValidTag(x))
    .slice(0, MAX_SERVICE_TAGS),
};

if (normalizedBirthDate) {
  accountPayload.birthDate = normalizedBirthDate;
}

      if (userId) {
        const accountRes = await fetch(
          `${API_URL}/api/account/${encodeURIComponent(userId)}`,
          {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
           body: JSON.stringify({
            username: title,
            avatar: avatarUrl,
            bio: subtitle,
            location,
            phone,
            showLocation,
            gender,
            profileType: isBusinessPlan
              ? profileType && profileType !== "personal"
                ? profileType
                : "business"
              : profileType,
            birthDate: displayDateToIso(birthDate),
            links: links
              .map((x) => ({
                label: String(x?.label || "").trim().slice(0, 50),
                url: normalizeUrl(String(x?.url || "").trim()),
              }))
              .filter((x) => x.url && isValidHttpUrl(x.url))
              .slice(0, MAX_LINKS),
            serviceCategories: serviceCategories.slice(0, MAX_SERVICE_CATEGORIES),
            serviceTags: serviceTags
              .map(normalizeTag)
              .filter((x) => x && isValidTag(x))
              .slice(0, MAX_SERVICE_TAGS),
          }),
        });

        const accountData = await accountRes.json().catch(() => ({}));

        if (!accountRes.ok) {
          throw new Error(accountData?.error || "Не вдалося зберегти основні дані");
        }
      }

      const payload = {
        title,
        subtitle,
        description,
        schedule,

        quickNavEnabled,

        quickNavOrder: normalizeOrder(modulesOrder)
          .filter((key) =>
            normalizeQuickNavOrder(
              quickNavOrder
            ).includes(key)
          ),

        modulesOrder:
          normalizeOrder(modulesOrder),

        enabledModules,
        theme: {
          accentColor,
          layout,
        },
        hero: {
          title,
          subtitle,
          image: avatarUrl,
        },
        about: {
          title: aboutTitle,
          text: aboutText,
        },
        seo: {
          title,
          description: subtitle || description,
        },
      };

      const res = await fetch(`${API_URL}/api/business/profile/me`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося зберегти");
      }

      setSuccess("Бізнес-профіль збережено");
      setTimeout(() => navigate("/profile"), 500);
    } catch (e) {
      console.error(e);
      setError(e.message || "Помилка збереження");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <DynamicHeader title="Бізнес-профіль" variant="glass" showBack withSpacer />
        <main className="business-edit">
          <div className="business-edit__loading">Завантаження...</div>
        </main>
      </>
    );
  }

  return (
    <>
      <DynamicHeader title="Бізнес-профіль" variant="glass" showBack withSpacer />

      <main className="business-edit">
        <div className="business-edit__content">
          {error && <div className="business-edit__error">{error}</div>}
          {success && <div className="business-edit__success">{success}</div>}

          <ProfileAccordionSection
            icon={<User size={22} strokeWidth={2.25} />}
            title="Основна інформація"
            description="Фото, назва, контакти та дані, за якими вас знайдуть клієнти."
            badge="Профіль"
            open={personalSectionOpen}
            onToggle={() =>
              setPersonalSectionOpen((current) => !current)
            }
          >
            <div className="business-edit__base">
            <div className="business-edit__avatar-wrapper">
              <label className="business-edit__ring">
                <svg className="business-edit__ring-svg" viewBox="0 0 120 120">
                  <circle
                    className="business-edit__ring-bg"
                    cx="60"
                    cy="60"
                    r="54"
                    fill="none"
                    strokeWidth="5"
                  />
                  <circle
                    className="business-edit__ring-fg"
                    cx="60"
                    cy="60"
                    r="54"
                    fill="none"
                    strokeWidth="5"
                    strokeDasharray="339.292"
                    strokeDashoffset="84.823"
                  />
                </svg>

                <span className="business-edit__avatar">
                  {avatarPreview ? (
                    <img src={avatarPreview} alt="" loading="eager" decoding="async" />
                  ) : (
                    <span>{(title || "B").slice(0, 1).toUpperCase()}</span>
                  )}
                </span>

                <span className="business-edit__ring-label">Бізнес</span>

                <input type="file" accept="image/*" onChange={onAvatarPick} />
              </label>
            </div>

            <div className="business-edit__info">
              <label className="business-edit__label">Назва бізнесу</label>
              <input
                className="business-edit__input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                maxLength={80}
                placeholder="Назва бізнесу"
              />
            </div>

            <div className="business-edit__info">
              <label className="business-edit__label">Короткий опис</label>
              <textarea
                className="business-edit__textarea"
                value={subtitle}
                onChange={(e) => setSubtitle(e.target.value)}
                maxLength={140}
                placeholder="Коротко про бізнес"
              />
              <div className="business-edit__char-counter">{subtitle.length}/140</div>
            </div>

            <div className="business-edit__preview-line">
              <div className="business-edit__preview-rating">
                <RatingStars value={rating} />
                <strong>{rating > 0 ? rating.toFixed(1) : "—"}</strong>
                <em>({reviewsCount})</em>
              </div>

              <div className="business-edit__preview-status">
                <Clock3 size={14} strokeWidth={2.4} />
                <span>{scheduleStatusText}</span>
              </div>
            </div>

            <div className="business-edit__info">
              <label className="business-edit__label">Локація</label>
              <div className="business-edit__input-icon">
                <MapPin size={17} strokeWidth={2.3} />
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Місто або адреса"
                />
              </div>
            </div>

            <div className="business-edit__info">
              <label className="business-edit__label">Телефон</label>
              <div className="business-edit__input-icon">
                <Phone size={17} strokeWidth={2.3} />
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+380..."
                />
              </div>
            </div>

            <label className="business-edit__check">
              <input
                type="checkbox"
                checked={showLocation}
                onChange={(e) => setShowLocation(e.target.checked)}
              />
              <span>Показувати локацію в профілі</span>
            </label>

                        <div className="business-edit__grid">
              <div className="business-edit__info">
                <label className="business-edit__label">Стать</label>
                <div className="business-edit__input-icon">
                  <User size={17} strokeWidth={2.3} />
                  <select
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <option value="">Не вказано</option>
                    <option value="male">Чоловіча</option>
                    <option value="female">Жіноча</option>
                    <option value="other">Інше</option>
                  </select>
                </div>
              </div>

              <div className="business-edit__info">
                <label className="business-edit__label">Дата народження</label>
                <div className="business-edit__input-icon">
                  <Cake size={17} strokeWidth={2.3} />
                  <input
                      inputMode="numeric"
                      value={birthDate}
                      onChange={handleBirthDateChange}
                      placeholder="дд.мм.рррр"
                      maxLength={10}
                    />
                </div>
              </div>
            </div>

                        <div className="business-edit__info">
              <label className="business-edit__label">Тип акаунта</label>

              <div className="beProfileChips">
                {PROFILE_TYPES.map((type) => {
                  const disabled = isBusinessPlan && type.value === "personal";
                  const active = profileType === type.value;

                  return (
                    <button
                      key={type.value}
                      type="button"
                      className={`beProfileChip ${active ? "is-active" : ""}`}
                      onClick={() => handleProfileTypePick(type.value)}
                      disabled={disabled}
                    >
                      {type.label}
                    </button>
                  );
                })}
              </div>

              {isBusinessPlan && (
                <div className="business-edit__hint">
                  Бізнес-підписка активна, тому особистий тип акаунта недоступний.
                </div>
              )}
            </div>

            <div className="business-edit__info">
              <label className="business-edit__label">
                Категорії спеціалізації
              </label>

              <div className="beProfileChips">
                {SERVICE_CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    className={`beProfileChip ${
                      serviceCategories.includes(cat.value) ? "is-active" : ""
                    }`}
                    onClick={() => toggleServiceCategory(cat.value)}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              <div className="business-edit__hint">
                Обрано {serviceCategories.length}/{MAX_SERVICE_CATEGORIES}
              </div>
            </div>

            <div className="business-edit__info">
              <label className="business-edit__label">Теги</label>

              <div className="beTagsRow">
                <input
                  className="business-edit__input"
                  value={serviceTagDraft}
                  onChange={(e) => setServiceTagDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addServiceTag();
                    }
                  }}
                  placeholder="Наприклад: консультації"
                />

                <button type="button" onClick={addServiceTag}>
                  Додати
                </button>
              </div>

              {!!serviceTags.length && (
                <div className="beSelectedTags">
                  {serviceTags.map((tag, idx) => (
                    <span key={`${tag}-${idx}`}>
                      {tag}
                      <button type="button" onClick={() => removeServiceTagAt(idx)}>
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              <div className="business-edit__hint">
                Обрано {serviceTags.length}/{MAX_SERVICE_TAGS}
              </div>
            </div>

            <div className="business-edit__info">
              <label className="business-edit__label">Посилання</label>

              <div className="beLinksGrid">
                <input
                  className="business-edit__input"
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  placeholder="Назва: Наприклад Instagram"
                />

                <input
                  className="business-edit__input"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="Посилання"
                />

                <button type="button" onClick={addLink}>
                  Додати
                </button>
              </div>

              {!!links.length && (
                <div className="beLinksList">
                  {links.map((link, idx) => (
                    <div key={`${link.url}-${idx}`} className="beLinksItem">
                      <div>
                        <strong>{link.label}</strong>
                        <span>{link.url}</span>
                      </div>

                      <button type="button" onClick={() => removeLinkAt(idx)}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

                            <div className="business-edit__hint">
                Додано {links.length}/{MAX_LINKS}
              </div>
            </div>
          </div>
        </ProfileAccordionSection>

        <ProfileAccordionSection
          icon={<BriefcaseBusiness size={22} strokeWidth={2.25} />}
          title="Налаштування бізнесу"
          description="Опишіть бізнес, налаштуйте графік, навігацію та потрібні модулі."
          badge="Інструменти"
          open={businessSectionOpen}
          onToggle={() =>
            setBusinessSectionOpen((current) => !current)
          }
        >
          <div className="beBusinessSettings">
            <div className="business-edit__info">

            <label className="business-edit__label">

              Кнопки для відвідувачів

            </label>



            <div className="beActionVisibility">

              <label>

                <input

                  type="checkbox"

                  checked={actionButtons.call}

                  onChange={() => toggleActionButton("call")}

                />



                <span>

                  <strong>Подзвонити</strong>

                  <em>Показувати кнопку дзвінка</em>

                </span>

              </label>



              <label>

                <input

                  type="checkbox"

                  checked={actionButtons.message}

                  onChange={() => toggleActionButton("message")}

                />



                <span>

                  <strong>Написати</strong>

                  <em>Дозволити перехід у чат</em>

                </span>

              </label>



              <label>

                <input

                  type="checkbox"

                  checked={actionButtons.booking}

                  onChange={() => toggleActionButton("booking")}

                />



                <span>

                  <strong>Записатись</strong>

                  <em>

                    Кнопка зʼявиться, лише якщо є послуги та онлайн-запис

                  </em>

                </span>

              </label>



              <label>

                <input

                  type="checkbox"

                  checked={actionButtons.share}

                  onChange={() => toggleActionButton("share")}

                />



                <span>

                  <strong>Поділитись</strong>

                  <em>Дозволити поширення профілю</em>

                </span>

              </label>

            </div>

          </div>
            <section className="business-edit__section beAboutSection">
              <div className="business-edit__section-head">
                <div>
                  <h2>Про бізнес</h2>
                  <p>
                    Розкажіть клієнтам, чим ви займаєтесь і чому варто обрати саме вас.
                  </p>
                </div>
              </div>

              <div className="business-edit__info">
                <label className="business-edit__label">
                  Опис бізнесу
                </label>
              <textarea
                className="business-edit__textarea business-edit__textarea--large"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1200}
                placeholder="Опиши бізнес, переваги, формат роботи..."
              />
                      <div className="business-edit__char-counter">
          {description.length}/1200
        </div>
      </div>
    </section>

          <section className="business-edit__section beScheduleSection">
            <div className="business-edit__section-head">
              <div>
                <h2>Графік роботи</h2>
                <p>Покажи клієнтам, коли бізнес відкритий.</p>
              </div>
            </div>

            <div className="esWorkingDaysRow">
              {WORKING_HOURS_DAYS.map((day) => {
                const active = !!workingHoursMap[day.key]?.enabled;

                return (
                  <button
                    key={day.key}
                    type="button"
                    className={`esWorkingDayPill ${active ? "is-active" : ""}`}
                    onClick={() => toggleWorkingDay(day.key)}
                  >
                    {day.label}
                  </button>
                );
              })}
            </div>

            <div className="esWorkingRows">
              {WORKING_HOURS_DAYS.map((day) => {
                const value = workingHoursMap[day.key];
                const active = !!value?.enabled;

                return (
                  <div key={day.key} className="esWorkingRow">
                    <div className="esWorkingDayLabel">{day.label}</div>

                    <div className="esWorkingRowMain">
                      {active ? (
                        <div className="esWorkingTimeRow">
                          <select
                            className="esWorkingTimeSelect"
                            value={value.start}
                            onChange={(e) =>
                              setWorkingTime(day.key, "start", e.target.value)
                            }
                          >
                            {TIME_OPTIONS.map((time) => (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            ))}
                          </select>

                          <span className="esWorkingArrow">—</span>

                          <select
                            className="esWorkingTimeSelect"
                            value={value.end}
                            onChange={(e) =>
                              setWorkingTime(day.key, "end", e.target.value)
                            }
                          >
                            {TIME_OPTIONS.map((time) => (
                              <option key={time} value={time}>
                                {time}
                              </option>
                            ))}
                          </select>

                          <button
                            type="button"
                            className="esWorkingTrash"
                            onClick={() => clearWorkingDay(day.key)}
                            aria-label="Зробити вихідним"
                          >
                            <X size={16} strokeWidth={2.5} />
                          </button>
                        </div>
                      ) : (
                        <div className="esWorkingOff">Вихідний</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="beQuickNavEditor">
  <div className="beQuickNavEditor__head">
    <div>
      <h2>Швидка навігація</h2>

      <p>
        Оберіть модулі, які показуватимуться
        у профілі. Затисніть пігулку, щоб
        змінити порядок.
      </p>
    </div>

    <label className="beQuickNavSwitch">
      <input
        type="checkbox"
        checked={quickNavEnabled}
        onChange={(event) =>
          setQuickNavEnabled(
            event.target.checked
          )
        }
      />

      <span />
    </label>
  </div>

  {quickNavEnabled && (
    <div className="beQuickNavPills">
      {sortedModules.map((module) => {
        const selected =
          quickNavOrder.includes(
            module.key
          );

        const dragging =
          quickNavDraggingKey ===
          module.key;

        return (
          <button
            key={module.key}
            type="button"
            className={[
              "beQuickNavPill",
              selected
                ? "is-selected"
                : "is-disabled",
              dragging
                ? "is-dragging"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            draggable={selected}
            onClick={() =>
              toggleQuickNavModule(
                module.key
              )
            }
            onDragStart={(event) => {
              if (!selected) {
                event.preventDefault();
                return;
              }

              event.dataTransfer.effectAllowed =
                "move";

              event.dataTransfer.setData(
                "text/plain",
                module.key
              );

              startQuickNavDrag(
                module.key
              );
            }}
            onDragOver={(event) => {
              if (!selected) return;

              event.preventDefault();

              moveQuickNavDrag(
                module.key
              );
            }}
            onDrop={(event) => {
              event.preventDefault();
              finishQuickNavDrag();
            }}
            onDragEnd={
              finishQuickNavDrag
            }
          >
            <span className="beQuickNavPill__icon">
              {module.icon}
            </span>

            <span className="beQuickNavPill__title">
              {module.title}
            </span>

            <span className="beQuickNavPill__state">
              {selected ? "✓" : "+"}
            </span>
          </button>
        );
      })}
    </div>
  )}
</section>

          <section className="business-edit__section business-edit__section--modules">
            <div className="business-edit__section-head beModulesHeading">
              <div>
                <h2>Бізнес-модулі</h2>

                <p>
                  Затисніть модуль і перетягніть,
                  щоб змінити порядок у профілі.
                </p>
              </div>

              <button
                type="button"
                className="business-edit__section-add"
                onClick={() =>
                  setModulePickerOpen(true)
                }
              >
                <Plus size={16} strokeWidth={2.4} />
                Додати
              </button>
            </div>

            {sortedModules.length === 0 ? (
              <div className="beEmptyModules">
                <div className="beEmptyModules__icon">
                  <BriefcaseBusiness size={28} strokeWidth={2.3} />
                </div>

                <strong>Поки немає жодного бізнес-модуля</strong>
                <span>
                  Натисни “Додати”, щоб підключити каталог, послуги, запис,
                  вакансії або інший блок.
                </span>
              </div>
            ) : (
              <div className="beModuleStack">
                {sortedModules.map((module, index) => {
                  const active = enabledModules[module.key] !== false;

                  return (
                    <ModuleWidget
                      key={module.key}
                      module={module}
                      active={active}
                      opened={
                        openedModuleKey === module.key
                      }
                      count={getModuleCount(module.key)}
                      index={index}
                      total={sortedModules.length}
                      onToggle={toggleModule}
                      onOpen={toggleOpenedModule}
                      onMove={moveModule}
                      onRemove={removeModule}
                      dragging={
                        draggingKey === module.key
                      }
                      onDragStart={onDragStart}
                      onDragOver={onDragOver}
                      onDragEnd={onDragEnd}
                    >
                      {module.key === "products" && (
                        <ProductsCatalog
                          products={productsDraft}
                          onAddProduct={openProductCreate}
                          ownerId={userId}
                        />
                      )}

                      {module.key === "services" && (
                        <ServicesCatalog
                          services={servicesDraft}
                          onAddService={openServiceCreate}
                          ownerId={userId}
                        />
                      )}

                      {module.key === "listings" && (
                        <BusinessListings
                          listings={listingsDraft}
                          ownerId={userId}
                          onOpenListing={(id) => navigate(`/listing/${id}`)}
                        />
                      )}

                      {module.key === "vacancies" && (
                        <Vacancies
                          vacancies={vacanciesDraft}
                          onAddVacancy={openVacancyCreate}
                          ownerId={userId}
                        />
                      )}

                      {module.key === "faq" && <FAQ ownerId={userId} />}

                      {module.key === "beforeAfter" && (
                        <BeforeAfter ownerId={userId} />
                      )}

                      {module.key === "booking" && <Booking ownerId={userId} />}

                      {module.key !== "products" &&
                      module.key !== "services" &&
                      module.key !== "listings" &&
                      module.key !== "vacancies" &&
                      module.key !== "faq" &&
                      module.key !== "beforeAfter" &&
                      module.key !== "booking" &&
                      module.key !== "posts" && (
                        <SimpleModulePreview module={module} />
                      )}

                    {module.key === "posts" && (
                      <SimpleModulePreview module={module} />
                    )}
                    </ModuleWidget>
                  );
                })}
              </div>
            )}
              </section>
          </div>
        </ProfileAccordionSection>

        <div className="business-edit__actions">
            <button
              type="button"
              className="business-edit__cancel"
              onClick={() => navigate("/profile")}
            >
              Скасувати
            </button>

            <button
              type="button"
              className="business-edit__save"
              onClick={save}
              disabled={saving}
            >
              {saving ? "Збереження..." : "Зберегти зміни"}
            </button>
          </div>
        </div>

              {modulePickerOpen && (
        <div className="beModulePicker is-open">
          <button
            type="button"
            className="beModulePicker__backdrop"
            onClick={() => setModulePickerOpen(false)}
            aria-label="Закрити"
          />

          <div className="beModulePicker__panel">
            <div className="beModulePicker__handle" />

            <div className="beModulePicker__head">
              <strong>Додати модуль</strong>

              <button type="button" onClick={() => setModulePickerOpen(false)}>
                <X size={20} strokeWidth={2.4} />
              </button>
            </div>

            <div className="beModulePicker__list">
              {MODULES.map((module) => {
                const added = !!enabledModules[module.key];

                return (
                  <button
                    key={module.key}
                    type="button"
                    className={added ? "is-added" : ""}
                    onClick={() => {
                      if (!added) addModule(module.key);
                    }}
                    disabled={added}
                  >
                    <span className="beModulePicker__icon">{module.icon}</span>

                    <span className="beModulePicker__text">
                      <strong>{module.title}</strong>
                      <em>{added ? "Вже додано" : module.desc}</em>
                    </span>

                    <span className="beModulePicker__plus">
                      {added ? "✓" : "+"}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

              <button
        type="button"
        className={`beFloatingAddModule ${modulePickerOpen ? "is-open" : ""}`}
        onClick={() => setModulePickerOpen((v) => !v)}
        aria-label="Додати модуль"
      >
        <Plus size={31} strokeWidth={2.35} />
      </button>

        <BusinessModuleCreate
          open={createModalOpen}
          moduleType={createModuleType}
          onClose={() => setCreateModalOpen(false)}
          onSubmit={handleModuleCreate}
        />
      </main>
    </>
  );
}